import { useEffect, useMemo, useRef, useState } from 'react';
import { useOtherSettings } from '@namespace/cms';
import { scrollToElement } from '@namespace/helpers';
import { useLocation } from '@namespace/i18n';

const maxWaitTime = 10000;
const linkTagRegex =
  /<link\s+rel=["']stylesheet["']\s+href=["'][^"']+["'][^>]*>/i;

export const usePrepareContent = (ref, markup) => {
  const { hash } = useLocation();
  const loadedCount = useRef(0);

  const { isSnippetsLoaderEnabled = false } = useOtherSettings();

  const markupHasStylesheets = useMemo(() => {
    return linkTagRegex.test(markup);
  }, [markup]);

  const [isLoaded, setIsLoaded] = useState(!markupHasStylesheets);

  const containerRef = ref;

  const showContent = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    // return undefined to avoid consistent-return eslint error
    if (!markup || !containerRef.current) {
      return undefined;
    }

    const slotHtml = document.createRange().createContextualFragment(markup); // Create a 'tiny' document and parse the html string
    containerRef.current.innerHTML = ''; // Clear the container
    containerRef.current.appendChild(slotHtml); // Append the new content

    // scroll to the snippet after loading it
    const hashFromSnippet = containerRef.current.querySelector(
      `[id='${hash.substring(1)}']` // hash without '#' we put in Id for compatibility with react-router-hash-link
    );
    if (hash && hashFromSnippet) {
      scrollToElement(hashFromSnippet, 'auto');
    }

    // search for external stylesheets
    const externalStylesheets = Array.from(
      containerRef.current.querySelectorAll('link[rel="stylesheet"]')
    );

    if (!externalStylesheets.length) {
      showContent();

      return undefined;
    }

    let timeout = null;

    const handleLoad = () => {
      loadedCount.current += 1;

      if (loadedCount.current === externalStylesheets.length) {
        showContent();
      }
    };

    externalStylesheets.forEach((link) => {
      link.addEventListener('load', handleLoad);
      link.addEventListener('error', handleLoad);
    });

    // if the stylesheets are not loaded after maxWaitTime, show the content anyway
    timeout = setTimeout(() => {
      if (!isLoaded) {
        showContent();
      }
    }, maxWaitTime);

    return () => {
      externalStylesheets.forEach((link) => {
        link.removeEventListener('load', handleLoad);
        link.removeEventListener('error', handleLoad);
      });

      clearTimeout(timeout);
    };
  }, [containerRef, hash, markup]); // don't add isLoaded in dependencies

  return useMemo(
    () => ({
      isLoaded,
      withLoader: markupHasStylesheets && isSnippetsLoaderEnabled
    }),
    [isLoaded, isSnippetsLoaderEnabled, markupHasStylesheets]
  );
};
